/**
 * DrugInteractionWarning Component
 * 
 * Displays potential drug interaction warnings with clear disclaimers
 * that this is NOT medical advice and users should consult their
 * doctor or pharmacist.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertTriangle,
  AlertCircle,
  Info,
  ShieldAlert,
  Phone,
  Stethoscope,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  SEVERITY,
  SEVERITY_COLORS,
  SEVERITY_LABELS,
  DISCLAIMER,
  getHighestSeverity,
} from '@/services/drugInteractionService';

const DrugInteractionWarning = ({
  isOpen,
  onClose,
  interactions,
  newMedication,
  onProceed,
  onCancel,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  const highestSeverity = getHighestSeverity(interactions);
  const hasMajor = interactions?.some(i => i.severity === SEVERITY.MAJOR);
  const hasModerate = interactions?.some(i => i.severity === SEVERITY.MODERATE);

  // Get icon based on severity
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case SEVERITY.MAJOR:
        return <ShieldAlert className="h-5 w-5" />;
      case SEVERITY.MODERATE:
        return <AlertTriangle className="h-5 w-5" />;
      case SEVERITY.MINOR:
        return <AlertCircle className="h-5 w-5" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  const getHeaderColors = () => {
    if (hasMajor) return 'bg-red-600';
    if (hasModerate) return 'bg-amber-500';
    return 'bg-yellow-500';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header with warning color */}
        <div className={`${getHeaderColors()} text-white p-4`}>
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2 text-white">
              <ShieldAlert className="h-6 w-6" />
              Potential Drug Interaction{interactions?.length > 1 ? 's' : ''} Found
            </DialogTitle>
            <DialogDescription className="text-white/90">
              Review this information and consult your healthcare provider
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Important Disclaimer Banner */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Stethoscope className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-1">
                  Not Medical Advice
                </h4>
                <p className="text-sm text-blue-800">
                  This is for <strong>informational purposes only</strong>. Always consult 
                  your <strong>doctor or pharmacist</strong> before starting, stopping, or 
                  changing any medication.
                </p>
              </div>
            </div>
          </div>

          {/* New Medication Being Added */}
          <div className="p-3 bg-slate-100 rounded-lg">
            <span className="text-sm text-slate-600">Adding medication:</span>
            <p className="font-semibold text-slate-900">{newMedication}</p>
          </div>

          {/* Interaction List */}
          <div className="space-y-3">
            <h4 className="font-medium text-slate-700">
              Potential Interactions ({interactions?.length || 0})
            </h4>
            
            {interactions?.map((interaction, index) => {
              const colors = SEVERITY_COLORS[interaction.severity] || SEVERITY_COLORS[SEVERITY.MINOR];
              const isExpanded = expandedIndex === index;
              
              return (
                <div
                  key={index}
                  className={`rounded-lg border ${colors.border} overflow-hidden`}
                >
                  {/* Interaction Header */}
                  <button
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    className={`w-full p-3 ${colors.bg} flex items-center justify-between text-left`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={colors.icon}>
                        {getSeverityIcon(interaction.severity)}
                      </span>
                      <div>
                        <span className={`font-medium ${colors.text}`}>
                          {SEVERITY_LABELS[interaction.severity] || 'Potential Interaction'}
                        </span>
                        <p className="text-sm text-slate-600">
                          {interaction.medication1} + {interaction.medication2}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-slate-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-500" />
                    )}
                  </button>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="p-3 bg-white border-t space-y-3">
                      {interaction.description && (
                        <div>
                          <span className="text-xs font-medium text-slate-500 uppercase">
                            Description
                          </span>
                          <p className="text-sm text-slate-700">{interaction.description}</p>
                        </div>
                      )}
                      
                      {interaction.effects && (
                        <div>
                          <span className="text-xs font-medium text-slate-500 uppercase">
                            Possible Effects
                          </span>
                          <p className="text-sm text-slate-700">{interaction.effects}</p>
                        </div>
                      )}
                      
                      {interaction.recommendation && (
                        <div className="p-2 bg-blue-50 rounded text-sm text-blue-800">
                          <strong>Recommendation:</strong> {interaction.recommendation}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Consult Healthcare Provider */}
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-emerald-900 mb-1">
                  Talk to Your Healthcare Provider
                </h4>
                <p className="text-sm text-emerald-800 mb-2">
                  {DISCLAIMER.action}
                </p>
                <p className="text-xs text-emerald-700">
                  Your doctor or pharmacist can review your complete medication list 
                  and health history to give you personalized advice.
                </p>
              </div>
            </div>
          </div>

          {/* Full Disclaimer */}
          <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
            <p>{DISCLAIMER.full}</p>
          </div>
        </div>

        {/* Footer Actions */}
        <DialogFooter className="p-4 border-t bg-slate-50 flex-shrink-0">
          <div className="w-full space-y-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  onCancel?.();
                  onClose();
                }}
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onProceed?.();
                  onClose();
                }}
                className={`flex-1 ${
                  hasMajor 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : hasModerate 
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                I Understand, Continue
              </Button>
            </div>
            <p className="text-xs text-center text-slate-500">
              By continuing, you acknowledge this is not medical advice
            </p>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DrugInteractionWarning;
